// src/features/attachment/upload.ts
// Multipart upload orchestration for large files (>MULTIPART_THRESHOLD_BYTES).
// Small files use uploadAttachment() from features/chat/logic.ts (same
// GetUploadUrl -> PUT -> ConfirmUpload flow, reused rather than duplicated).

import { attachmentApi } from '@core/api';
import type { CompletedPart } from '@core/types/attachment';
import { MULTIPART_PART_SIZE_BYTES } from './logic';

function putPart(url: string, blob: Blob, onProgress?: (loaded: number) => void): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag = xhr.getResponseHeader('ETag') ?? '';
        resolve(etag);
      } else {
        reject(new Error(`Part upload failed: ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error('Part upload network error'));
    xhr.send(blob);
  });
}

export interface MultipartUploadHandle {
  uploadId: string;
  abort: () => Promise<void>;
}

/**
 * Upload a large file via the multipart flow: Initiate -> PUT each part ->
 * Complete. Parts upload sequentially (simple and predictable for progress
 * reporting); on any failure the upload is aborted server-side so storage
 * doesn't accumulate orphaned parts.
 */
export async function uploadAttachmentMultipart(
  file: File,
  onProgress?: (percent: number) => void,
): Promise<{ entityId: string }> {
  const partCount = Math.max(1, Math.ceil(file.size / MULTIPART_PART_SIZE_BYTES));

  const initiateRes = await attachmentApi.InitiateMultipartUpload({
    file_name: file.name,
    mime_type: file.type || 'application/octet-stream',
    size_bytes: file.size,
    part_count: partCount,
  });

  const uploadId = initiateRes.upload_id;
  if (!uploadId) throw new Error('No multipart upload ID received');

  const abort = async () => {
    try {
      await attachmentApi.AbortMultipartUpload({ upload_id: uploadId });
    } catch {
      // best-effort cleanup
    }
  };

  try {
    const parts = initiateRes.parts ?? [];
    const completed: CompletedPart[] = [];
    let uploadedBytes = 0;

    for (let i = 0; i < partCount; i++) {
      const start = i * MULTIPART_PART_SIZE_BYTES;
      const end = Math.min(start + MULTIPART_PART_SIZE_BYTES, file.size);
      const blob = file.slice(start, end);
      const partNumber = i + 1;

      let uploadUrl = parts.find((p) => p.part_number === partNumber)?.upload_url?.url;
      if (!uploadUrl) {
        const partUrlRes = await attachmentApi.GetMultipartUploadPartUrl({
          upload_id: uploadId,
          part_number: partNumber,
        });
        uploadUrl = partUrlRes.upload_url?.url;
      }
      if (!uploadUrl) throw new Error(`No upload URL for part ${partNumber}`);

      const partSize = end - start;
      const etag = await putPart(uploadUrl, blob, (loaded) => {
        onProgress?.(Math.round(((uploadedBytes + loaded) / file.size) * 100));
      });
      uploadedBytes += partSize;
      onProgress?.(Math.round((uploadedBytes / file.size) * 100));
      completed.push({ part_number: partNumber, etag });
    }

    const completeRes = await attachmentApi.CompleteMultipartUpload({
      upload_id: uploadId,
      parts: completed,
    });

    const entityId = completeRes.attachment?.entity?.id;
    if (!entityId) throw new Error('No attachment entity ID returned');
    return { entityId };
  } catch (err) {
    await abort();
    throw err;
  }
}
